import firmadokLoginImage from '@/assets/images/firmadok_login_image.jpg'
import firmadokLogo from '@/assets/images/MainLogo.svg'
import { Button } from '@/components/ui'
import type { RespondToCompanyInvitationMutation } from '@/modules/authentication'
import {
  RESPOND_TO_COMPANY_INVITATION_MUTATION,
  useJwtTokenFromUrlId,
} from '@/modules/authentication'
import { fromGlobalId } from '@/utils'
import { useOnErrorAuto } from '@/utils/hooks'
import { useMutation } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isBefore, isValid } from 'date-fns'
import React, { useState } from 'react'
import { Link } from 'react-router'

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid h-full min-h-[600px] grid-cols-1 md:grid-cols-[500px_1fr]">
    <div className="relative box-border h-full bg-[#f7f8fc] px-6 py-12 md:px-12 md:py-16">
      {children}
      <div className="absolute bottom-8 left-6 text-xs text-gray-400 md:left-12">
        &copy; TACtrl.ai AS, {new Date().getFullYear()}
      </div>
    </div>
    <div
      className="hidden bg-cover bg-center md:block"
      style={{ backgroundImage: `url(${firmadokLoginImage})` }}
    />
  </div>
)

interface AcceptCompanyInvitationProps {
  /**
   * Relay id of the currently logged-in user, set when the page is rendered
   * inside the authenticated app (from Bootstrap) instead of NoAuthRoutes.
   */
  loggedInUserId?: string
}

export const AcceptCompanyInvitation: React.FC<
  AcceptCompanyInvitationProps
> = ({ loggedInUserId }) => {
  const translations = useTranslate({
    title: 'users.accept-invitation.title',
    description: 'users.accept-invitation.description',
    accept: 'users.accept-invitation.accept',
    decline: 'users.accept-invitation.decline',
    accepted: ['users.accept-invitation.accepted', { company: '' }],
    acceptedLoggedIn: [
      'users.accept-invitation.accepted-logged-in',
      { company: '' },
    ],
    declined: 'users.accept-invitation.declined',
    wrongAccount: 'users.accept-invitation.wrong-account',
    goBack: 'reset-password.go-back',
    goToDashboard: 'users.accept-invitation.go-to-dashboard',
    errors: {
      badToken: 'reset-password.errors.bad-token',
      expiredToken: 'reset-password.errors.expired-token',
      generic: 'error.generic-server-error',
    },
  })

  const { token, tokenData, expirationDate, tokenIsValid } =
    useJwtTokenFromUrlId()
  const onErrorAuto = useOnErrorAuto()

  const [remoteError, setRemoteError] = useState<string | null>(null)
  const [result, setResult] = useState<{
    accepted: boolean
    companyName: string | null
  } | null>(null)

  const [respond, { loading }] =
    useMutation<RespondToCompanyInvitationMutation>(
      RESPOND_TO_COMPANY_INVITATION_MUTATION,
      {
        onError: onErrorAuto(
          {},
          {
            showToast: false,
            callback({ message }: { message: string }) {
              if (
                message.includes('bad action') ||
                message.includes('Invalid token')
              ) {
                setRemoteError(translations.errors.badToken)
              } else if (message.includes('expired')) {
                setRemoteError(translations.errors.expiredToken)
              } else {
                setRemoteError(translations.errors.generic)
              }
            },
          }
        ),
      }
    )

  async function handleRespond(accept: boolean) {
    const { data } = await respond({ variables: { token, accept } })
    if (data?.respondToCompanyInvitation?.ok) {
      setResult({
        accepted: accept,
        companyName: data.respondToCompanyInvitation.companyName,
      })
    }
  }

  // Plain anchor when logged in: a full page load re-runs the bootstrap
  // query, so a freshly accepted company shows up in the company switcher.
  const backLink = loggedInUserId ? (
    <a href="/" className="inline-block pt-8 text-xs text-gray-500 underline">
      {translations.goToDashboard}
    </a>
  ) : (
    <Link
      to="/login"
      className="inline-block pt-8 text-xs text-gray-500 underline"
    >
      {translations.goBack}
    </Link>
  )

  // Shallow token validation, mirroring the create-password flow.
  const tokenExpired =
    !isValid(expirationDate) || isBefore(expirationDate, new Date())

  // The token is the credential, but a logged-in user shouldn't answer an
  // invitation that was sent to a different account.
  const loggedInPk = loggedInUserId
    ? fromGlobalId(loggedInUserId)?.[1] ?? null
    : null
  const wrongAccount =
    loggedInPk !== null &&
    typeof tokenData?.user_id === 'number' &&
    tokenData.user_id !== loggedInPk

  let content: React.ReactNode
  if (remoteError) {
    content = (
      <>
        <p className="mt-8 text-lg text-red-600">{remoteError}</p>
        {backLink}
      </>
    )
  } else if (tokenIsValid) {
    content = (
      <>
        <p className="mt-8 text-lg text-red-600">
          {translations.errors.badToken}
        </p>
        {backLink}
      </>
    )
  } else if (tokenExpired) {
    content = (
      <>
        <p className="mt-8 text-lg text-red-600">
          {translations.errors.expiredToken}
        </p>
        {backLink}
      </>
    )
  } else if (wrongAccount) {
    content = (
      <>
        <p className="mt-8 text-lg text-red-600">{translations.wrongAccount}</p>
        {backLink}
      </>
    )
  } else if (result) {
    content = (
      <>
        <img src={firmadokLogo} alt="TaCtrl" className="h-[50px]" />
        <p className="mt-8 text-2xl font-normal text-gray-700">
          {result.accepted
            ? loggedInUserId
              ? translations.acceptedLoggedIn({
                  company: result.companyName ?? '',
                })
              : translations.accepted({ company: result.companyName ?? '' })
            : translations.declined}
        </p>
        {backLink}
      </>
    )
  } else {
    content = (
      <>
        <img src={firmadokLogo} alt="TaCtrl" className="h-[50px]" />
        <h4 className="mt-8 text-2xl font-normal text-gray-700">
          {translations.title}
        </h4>
        <p className="mt-6 mb-8 max-w-full text-gray-500">
          {translations.description}
        </p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button disabled={loading} onClick={() => void handleRespond(true)}>
            {translations.accept}
          </Button>
          <Button
            variant="outline"
            disabled={loading}
            onClick={() => void handleRespond(false)}
          >
            {translations.decline}
          </Button>
        </div>
      </>
    )
  }

  return <Shell>{content}</Shell>
}
